import { useEffect, useState } from 'react';

type LegalConsentShieldProps = {
  shopDomain: string;
};

export function LegalConsentShield({ shopDomain }: LegalConsentShieldProps) {
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleLock = () => setLocked(true);
    window.addEventListener('reach:legal-consent-required', handleLock);
    return () => window.removeEventListener('reach:legal-consent-required', handleLock);
  }, []);

  if (!locked) return null;

  const handleAgree = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/merchant/legal/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeDomain: shopDomain }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(data?.error || 'Failed to record consent.');
      }

      window.location.reload();
    } catch (err: any) {
      setError(err?.message || 'An error occurred. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-white/95 p-6 backdrop-blur">
      <div className="w-full max-w-[520px] rounded-[28px] border border-slate-100 bg-white p-10 text-center shadow-2xl shadow-slate-200/80">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 text-2xl">
          !
        </div>
        <h2 className="mb-3 text-2xl font-extrabold tracking-tight text-slate-950">
          Legal Compliance Required
        </h2>
        <p className="mb-8 text-[15px] leading-6 text-slate-500">
          To protect your store and your customers, we require all merchants to review and agree to our updated legal framework before accessing Reach.
        </p>
        <div className="mb-8 flex flex-col gap-3 rounded-2xl bg-slate-50 p-5 text-left text-sm font-semibold">
          <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-cyan-700 hover:text-cyan-900">Terms of Service -&gt;</a>
          <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-cyan-700 hover:text-cyan-900">Privacy Policy -&gt;</a>
          <a href="/legal/dpa" target="_blank" rel="noreferrer" className="text-cyan-700 hover:text-cyan-900">Data Processing Addendum -&gt;</a>
        </div>
        {error && <p className="mb-4 text-sm font-semibold text-red-600">{error}</p>}
        <button
          type="button"
          onClick={handleAgree}
          disabled={submitting}
          className="w-full rounded-2xl bg-slate-950 px-5 py-4 text-sm font-extrabold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Saving...' : 'I Agree and Continue'}
        </button>
      </div>
    </div>
  );
}
